import {
  deDE as dataGridDe,
  enUS as dataGridEn,
} from "@mui/x-data-grid-pro/locales";
import {
  deDE as datePickersDe,
  enUS as datePickersEn,
} from "@mui/x-date-pickers-pro/locales";
import type { Locale } from "date-fns/locale";
import { de as deLocale } from "date-fns/locale/de";
import { enUS as enLocale } from "date-fns/locale/en-US";
import { createContext, useContext, useEffect, useState } from "react";
import { useLocalStorage } from "~components/shared/useLocalStorage";
import { handleError } from "~lib/handleError.ts";
import { importWithRetries } from "~lib/lazyWithRetry.ts";
import { de } from "~translations/de.ts";
import type { en } from "~translations/en.ts";

// German is the primary language and stays in the start graph; English (117 KB) loads when someone switches
type Messages = typeof de | typeof en;

const translationsMap = {
  de: {
    locale: deLocale,
    datePickers:
      datePickersDe.components.MuiLocalizationProvider.defaultProps.localeText,
    dataGrid: dataGridDe.components.MuiDataGrid.defaultProps.localeText,
  },
  en: {
    locale: enLocale,
    datePickers:
      datePickersEn.components.MuiLocalizationProvider.defaultProps.localeText,
    dataGrid: dataGridEn.components.MuiDataGrid.defaultProps.localeText,
  },
};

type DatePickersTranslations = (typeof translationsMap)["de"]["datePickers"];
type DataGridTranslations = (typeof translationsMap)["de"]["dataGrid"];

type Language = keyof typeof translationsMap;

export const availableLanguages = Object.keys(translationsMap) as Language[];
export type Translations = {
  [key in keyof Messages]: Messages[key];
};
export type TranslationKey = keyof Translations;

type NonNullableKeys<T> = {
  [K in keyof T]: T[K] extends null ? never : K;
}[keyof T];

export type NonNullTranslationKey = NonNullableKeys<Translations>;

interface I18nContextType {
  lang: Language;
  setLang: (value: Language | ((val: Language) => Language)) => void;
  locale: Locale;
  translations: Translations;
  datePickersTranslations: DatePickersTranslations;
  dataGridTranslations: DataGridTranslations;
}

const NullI18nContext: I18nContextType = {
  lang: "de",
  setLang: () => {},
  locale: translationsMap.de.locale,
  translations: {} as unknown as Translations,
  datePickersTranslations: {} as unknown as DatePickersTranslations,
  dataGridTranslations: {} as DataGridTranslations,
};

const I18nContext = createContext<I18nContextType>(NullI18nContext);

interface I18nProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
}

export function I18nProvider({
  children,
  defaultLanguage = "de",
}: I18nProviderProps) {
  const [lang, setLang] = useLocalStorage<Language>("lang", defaultLanguage);

  const [enMessages, setEnMessages] = useState<Messages>();
  useEffect(() => {
    if (lang !== "en" || enMessages) return;
    let active = true;
    importWithRetries(() => import("~translations/en.ts"))
      .then((m) => {
        if (active) setEnMessages(m.en);
      })
      .catch(handleError);
    return () => {
      active = false;
    };
  }, [lang, enMessages]);

  // lang is the user's choice; texts, locale and html lang switch together once the messages are loaded
  const activeLang: Language = lang === "en" && enMessages ? "en" : "de";
  const { locale, dataGrid, datePickers } = translationsMap[activeLang];
  const translations = activeLang === "en" && enMessages ? enMessages : de;

  useEffect(() => {
    document.documentElement.lang = activeLang;
  }, [activeLang]);

  return (
    <I18nContext.Provider
      value={{
        lang,
        setLang,
        locale,
        translations,
        datePickersTranslations: datePickers,
        dataGridTranslations: dataGrid,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const i18n = useContext<I18nContextType>(I18nContext);
  if (!i18n) throw new Error("Missing I18nProvider");
  return i18n;
}

export function useTranslation() {
  return useI18n().translations;
}

export function useLocale() {
  return useI18n().locale;
}
